import React from 'react'
import styled, { css } from 'styled-components'

import ColumnGridPanel from 'components/ColumnGridPanel'

import { degrees, degreesFahrenheit, ratioToPercent, friendlyName } from 'helpers/formatters'
import { FRIENDLY_NAMES } from 'helpers/reportFriendlyNames'

import { color } from 'styles/colors'

type Props = {
  mode: string
  coolSetpoint: string
  heatSetpoint: string
  indoorTemperature: string
  relativeHumidity: number
  outdoorTemperature: string
  hasAwairDevice: boolean
  displayOperationStatus: boolean
  operationStatus: string
  className: string
}

const ThermostatBox = ({ mode, coolSetpoint, heatSetpoint, indoorTemperature, relativeHumidity, outdoorTemperature, hasAwairDevice, displayOperationStatus, operationStatus, className }: Props) => {
  return (
    <ColumnGridPanel.Column {...{ className }}>
      <S.ThermostatBox aria-label="thermostat box">
        <S.Row>
          <div>
            <S.IndoorTemperatureContainer>
              <S.IndoorTemperature>
                {degrees(indoorTemperature, 1) || '--'}
              </S.IndoorTemperature>
              <S.RelativeHumidity>
                <i className="icon-droplet" />
                {ratioToPercent(relativeHumidity) || '--'}
              </S.RelativeHumidity>

              {displayOperationStatus && <S.OperationStatus>{friendlyName(operationStatus, FRIENDLY_NAMES)}</S.OperationStatus>}
            </S.IndoorTemperatureContainer>
            <S.OutdoorTemperature>
              <S.DisplayLabel>Outdoor Temp</S.DisplayLabel>
              <S.DisplayValue>{degreesFahrenheit(outdoorTemperature) || '--'}</S.DisplayValue>
            </S.OutdoorTemperature>
          </div>
          <S.Setpoints>
            {mode && (mode.match(/COOL/gi) || mode.match(/AUTO/gi)) && <S.CoolSetpoint >{degrees(coolSetpoint) || '--'}</S.CoolSetpoint>}
            {mode && (mode.match(/HEAT/gi) || mode.match(/AUTO/gi)) && <S.HeatSetpoint>{degrees(heatSetpoint) || '--'}</S.HeatSetpoint>}
          </S.Setpoints>
        </S.Row>
      </S.ThermostatBox>

      <S.SystemBadges>
        { hasAwairDevice && <S.AwairIcon src="/img/awair_badge.svg" alt="awair-icon" aria-label="awair icon" /> }
      </S.SystemBadges>
    </ColumnGridPanel.Column>
  )
}

export default ThermostatBox

const setpointStyle = () => {
  return css`
    font-size: 2rem;
    font-weight: bold;
    padding: 2px 15px;
    border-radius: 10px;
  `
}

const S = {
  ThermostatBox: styled.div`
    width:  300px;
    border-radius: 10px;
    background: ${color('DashboardThermostatBackground')};
    text-align: center;
    color: ${color('DashboardThermostatText')};
    margin: 5% auto;
    border: 1px solid ${color('DashboardThermostatBorder')};
  `,
  Row: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 15px;
  `,
  IndoorTemperatureContainer: styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: flex-start;
  `,
  IndoorTemperature: styled.div`
    font-size:  48px;
    font-weight: bold;
    color: ${color('DashboardThermostatText')};
  `,
  RelativeHumidity: styled.div`
    font-size: 1rem;

    .icon-droplet {
      color:${color('iconDropletBackground')};
    }

    &:after {
      content: ' RH';
      font-size: 1rem;
    }
  `,
  OperationStatus: styled.div`
    font-size: 1rem;
  `,
  Setpoints: styled.div`
    margin-right: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    color: ${color('DashboardThermostatSetpont')};
  `,
  CoolSetpoint: styled.div`
    ${setpointStyle}
    background: ${color('thermostatBoxCoolSetpoint')};
  `,
  HeatSetpoint: styled.div`
    ${setpointStyle}
    background: ${color('thermostatBoxHeatSetpoint')};
  `,
  OutdoorTemperature: styled.div`
    text-transform: capitalize;
    font-size: 1rem;
    padding-top: 5px;
    padding-bottom: 5px;
    white-space: nowrap;
    text-align: center;
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 10px;
  `,
  DisplayLabel: styled.div`
    text-align: left;
    font-size: 1rem;
    color: ${color('DashboardThermostatText')};
  `,
  DisplayValue: styled.div`
    text-align: left;
  `,
  SystemBadges: styled.div`
    display: flex;
  `,
  AwairIcon: styled.img`
    width: 100%;
    max-width: 138px;
    margin: 0px auto;
  `
}
